import React from 'react';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import classnames from 'classnames';

import CardHeader from '~/components/CardHeader/CardHeader';
import TableRecord from '~/components/TableRecord/TableRecord';
import StyledComponent from '~/components/Styled/Styled';

import css from '~/components/TeamSchedule/TeamSchedule.scss';
import LiveNowChip from '~/components/LiveNowChip/LiveNowChip';

const cssContext = classnames.bind(css);
const getEventDate = (event: Event) => moment(event.game_date).format('MMM D');
const getEventTime = (event: Event) => moment(event.game_date).format('h:mm a');
const getEventScore = (score: Score, team: Team) => {
  const homeScore = get(score, 'home.score', '');
  const awayScore = get(score, 'away.score', '');

  if (score.winning_team && score.winning_team === team.api_uri) {
    return `W ${Math.max(homeScore, awayScore)} - ${Math.min(homeScore, awayScore)}`;
  }

  if (score.losing_team && score.losing_team === team.api_uri) {
    return `L ${Math.min(homeScore, awayScore)} - ${Math.max(homeScore, awayScore)}`;
  }

  return `T ${homeScore} - ${homeScore}`;
};
const getOvertime = (progress: Progress) => {
  if (progress.overtime) {
    return '(OT)';
  }

  return '';
};

const getOpponentInfo = (event: Event, team: Team) => {
  const awayTeamName = get(event, 'away_team.short_name', '');
  const homeTeamName = get(event, 'home_team.short_name', '');
  if (team.api_uri === get(event, 'away_team.api_uri', '')) {
    return `@ ${homeTeamName}`;
  }
  return `vs ${awayTeamName}`;
};

const eventLog = (
  event: Event,
  team: Team,
  mostRecentEvent: Event,
  recentRef: (htmlDivElement) => typeof htmlDivElement
) => {
  const final = event.event_status === 'final';
  const inProgress = event.event_status === 'in_progress';
  const isRecentEvent = event.api_uri === get(mostRecentEvent, 'api_uri', '');
  const eventClasses = cssContext(css.scheduleRow, {
    [css.recentEvent]: isRecentEvent,
    row: true,
  });

  if (!event.event_status) {
    return null;
  }

  return (
    <TableRecord key={event.api_uri} api_uri={event.api_uri}>
      <div
        className={eventClasses}
        id={isRecentEvent ? 'active' : null}
        ref={isRecentEvent ? recentRef : null}
      >
        <div className="col-xs-4">{getEventDate(event)}</div>
        <div className="col-xs-4">{getOpponentInfo(event, team)}</div>
        <div className="col-xs-4">
          {!final && !inProgress && getEventTime(event)}
          {inProgress && <LiveNowChip />}
          {final &&
            `${getEventScore(get(event, 'box_score.score', {}), team)} ${getOvertime(
              get(event, 'box_score.progress', {})
            )}`}
        </div>
      </div>
    </TableRecord>
  );
};

type Props = {
  events: any;
  team: Team;
  mostRecentEvent: Event;
  recentRef: (htmlDivElement) => typeof htmlDivElement;
};

class TeamSchedule extends React.Component<Props> {
  static displayName: string;

  componentDidMount() {
    setTimeout(() => {
      const teamScheduleComponent = document.getElementById('teamScheduleComponent');
      const activeElem = document.getElementById('active');

      if (teamScheduleComponent && activeElem) {
        const { top: activeElemTop } = activeElem.getBoundingClientRect();
        const {
          top: teamScheduleComponentTop,
          height: teamScheduleComponentHeight,
        } = teamScheduleComponent.getBoundingClientRect();

        teamScheduleComponent.scrollTo({
          top: activeElemTop - teamScheduleComponentTop - teamScheduleComponentHeight / 2,
          behavior: 'smooth',
        });
      }
    }, 500);
  }

  renderEvents(title, events) {
    return (
      <div className={css.teamSchedule}>
        <h3>{title}</h3>
        {this.renderEventsByMonth(events)}
      </div>
    );
  }

  renderEventsByMonth(events) {
    return Object.keys(events).map(monthAndYear => (
      <div key={monthAndYear}>
        <CardHeader>
          <div>{monthAndYear}</div>
        </CardHeader>
        <div className={css.events}>
          {events[monthAndYear].map(event =>
            eventLog(event, this.props.team, this.props.mostRecentEvent, this.props.recentRef)
          )}
        </div>
      </div>
    ));
  }

  render() {
    if (!Array.isArray(this.props.events)) {
      return null;
    }

    const eventIndex = {
      preSeason: ['Spring', 'Exhibition', 'Preseason', 'Cup Short'],
      regularSeason: ['Regular Season', 'Domestic league'],
      postSeason: ['Postseason', 'Playoffs', 'Postseason Tournament'],
    };

    const eventsByGameType = this.props.events.reduce(
      (acc, elem) => {
        if (eventIndex.preSeason.includes(elem.game_type)) {
          acc.preSeason.push(elem);
        } else if (eventIndex.regularSeason.includes(elem.game_type)) {
          acc.regularSeason.push(elem);
        } else if (eventIndex.postSeason.includes(elem.game_type)) {
          acc.postSeason.push(elem);
        }

        return acc;
      },
      {
        preSeason: [],
        regularSeason: [],
        postSeason: [],
      }
    );

    const preSeasonEvents = groupBy(eventsByGameType.preSeason, (event: Event) =>
      moment(event.game_date).format('MMMM YYYY')
    );

    const regularSeasonEvents = groupBy(eventsByGameType.regularSeason, (event: Event) =>
      moment(event.game_date).format('MMMM YYYY')
    );

    const postSeasonEvents = groupBy(eventsByGameType.postSeason, (event: Event) =>
      moment(event.game_date).format('MMMM YYYY')
    );

    return (
      <div>
        {Object.keys(preSeasonEvents).length > 0
          ? this.renderEvents('Pre-Season', preSeasonEvents)
          : null}

        {Object.keys(regularSeasonEvents).length > 0
          ? this.renderEvents('Regular Season', regularSeasonEvents)
          : null}

        {Object.keys(postSeasonEvents).length > 0
          ? this.renderEvents('Post-Season', postSeasonEvents)
          : null}
      </div>
    );
  }
}

TeamSchedule.displayName = 'TeamSchedule';

export default StyledComponent(TeamSchedule, [css]);
